import { Accordion, Anchor, H1, H3, Tabs } from '@dnb/eufemia';
import { type AppApiEntitlementRequestResponseDto, ReviewStatus } from '@portals/shared/admin/AppApiEntitlementDto';
import { enUS } from 'date-fns/locale';
import { type JSX, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import useSWR from 'swr';

import BackButton from '@/components/BackButton';
import DataTable, { type Column } from '@/components/DataTable';
import LoadingPage from '@/components/LoadingPage';
import { humanDate } from '@/utils';

const COLUMNS: Column<EntitlementRequestRow>[] = [
  {
    header: '',
    attribute: 'id',
    render: ({ id }) => (
      <Anchor element={Link} to={`/entitlements/${id}`}>
        View
      </Anchor>
    ),
  },
  { header: 'App', attribute: 'app' },
  { header: 'Owner', attribute: 'owner' },
  { header: 'Organization', attribute: 'organization' },
  { header: 'Api', attribute: 'api' },
  {
    header: 'Requsted',
    attribute: 'requested',
    render: ({ requested }) => humanDate(requested),
  },
  {
    header: 'Updated',
    attribute: 'updated',
    render: ({ updated }) => humanDate(updated),
  },
];

const TABS = [
  { title: 'Approved by API provider', key: ReviewStatus.LEVEL1_APPROVED },
  { title: 'Presubmit', key: ReviewStatus.PRESUBMIT },
  { title: 'Pending', key: ReviewStatus.PENDING },
  { title: 'Approved', key: ReviewStatus.APPROVED },
  { title: 'Rejected', key: ReviewStatus.DENIED },
  { title: 'Canceled', key: ReviewStatus.CANCELED },
];

interface EntitlementRequestRow {
  id: string;
  app: string;
  owner: string;
  organization?: string;
  api: string;
  requested: string;
  updated: string;
}

export default function EntitlementRequestsList(): JSX.Element {
  const [activeTab, setActiveTab] = useState<ReviewStatus>(ReviewStatus.LEVEL1_APPROVED);
  const { data: entitlements, isValidating: entitlementsLoading } =
    useSWR<AppApiEntitlementRequestResponseDto[]>('/entitlement-requests');

  const chartData = useMemo(() => {
    if (!entitlements) {
      return { labels: [], datasets: [] };
    }

    // Sort by requested date
    const sortedData = [...entitlements].sort(
      (a, b) => new Date(a.requested).getTime() - new Date(b.requested).getTime(),
    );

    // Build cumulative counts
    let cumulativeCount = 0;
    const cumulativeData = sortedData.map((entitlement) => {
      cumulativeCount += 1;
      return {
        date: entitlement.requested,
        count: cumulativeCount,
      };
    });

    return {
      labels: cumulativeData.map((d) => d.date),
      datasets: [
        {
          label: 'Entitlement Requests',
          data: cumulativeData.map((d) => d.count),
          borderColor: '#007272',
          backgroundColor: '#A5E1D280',
          fill: true,
        },
      ],
    };
  }, [entitlements]);

  const rows = useMemo(() => {
    if (!entitlements) {
      return [];
    }

    const rowsByStatus = (status: ReviewStatus) =>
      entitlements
        .filter(({ reviewStatus }) => reviewStatus === status)
        .map<EntitlementRequestRow>((entitlement) => ({
          ...entitlement,
          app: entitlement.appName,
          api: entitlement.apiName,
        })) ?? [];

    switch (activeTab) {
      case ReviewStatus.PRESUBMIT: {
        return rowsByStatus(ReviewStatus.PRESUBMIT);
      }
      case ReviewStatus.PENDING: {
        return rowsByStatus(ReviewStatus.PENDING);
      }
      case ReviewStatus.LEVEL1_APPROVED: {
        return rowsByStatus(ReviewStatus.LEVEL1_APPROVED);
      }
      case ReviewStatus.APPROVED: {
        return rowsByStatus(ReviewStatus.APPROVED);
      }
      case ReviewStatus.DENIED: {
        return rowsByStatus(ReviewStatus.DENIED);
      }
      default: {
        return rowsByStatus(ReviewStatus.CANCELED);
      }
    }
  }, [activeTab, entitlements]);

  if (entitlementsLoading) {
    return <LoadingPage />;
  }

  return (
    <>
      <BackButton to="/">Home</BackButton>

      <H1 top="large">Entitlement Requests</H1>
      <Accordion
        bottom="small"
        expanded
        id="entitlement-requests-accordion"
        remember_state
        title="Entitlement requests graph"
      >
        <H3>Requests by Date</H3>
        <Line
          data={{ datasets: chartData.datasets, labels: chartData.labels }}
          options={{
            responsive: true,
            scales: {
              x: {
                type: 'time' as const,
                adapters: {
                  date: {
                    locale: enUS,
                  },
                },
              },
            },
          }}
        />
      </Accordion>
      <Tabs data={TABS} on_change={({ key }) => setActiveTab(key)} selected_key={activeTab}>
        <DataTable
          columns={COLUMNS}
          data={rows}
          defaultSortKey="requested"
          filterBy={['app', 'api', 'organization', 'owner']}
          reverseSort
        />
      </Tabs>
    </>
  );
}
